// State management module for ChatWidget

// Storage key prefix - will be set based on propertyID to isolate sessions
let storageKeyPrefix = 'chat_widget';

// Get the current storage key prefix
export function getStorageKeyPrefix() {
    return storageKeyPrefix;
}

// Set the storage key prefix based on propertyID
// This should be called when propertyID becomes known
export function setStorageKeyPrefix(propertyID) {
    if (propertyID) {
        storageKeyPrefix = `chat_widget_${propertyID}`;
    } else {
        storageKeyPrefix = 'chat_widget';
    }
}

// Get a storage key with the current prefix
function getKey(name) {
    return `${storageKeyPrefix}_${name}`;
}

// Load messages from sessionStorage
export function loadMessages() {
    try {
        const saved = sessionStorage.getItem(getKey('messages'));
        return saved ? JSON.parse(saved) : [];
    } catch (error) {
        console.error('Error loading messages:', error);
        return [];
    }
}

// Save messages to sessionStorage
export function saveMessages(messages) {
    try {
        sessionStorage.setItem(getKey('messages'), JSON.stringify(messages));
    } catch (error) {
        console.error('Error saving messages:', error);
    }
}

// Load user details from sessionStorage
export function loadUserDetails() {
    try {
        const saved = sessionStorage.getItem(getKey('user_details'));
        return saved ? JSON.parse(saved) : null;
    } catch (error) {
        console.error('Error loading user details:', error);
        return null;
    }
}

// Save user details to sessionStorage
export function saveUserDetails(details) {
    try {
        sessionStorage.setItem(getKey('user_details'), JSON.stringify(details));
        return details;
    } catch (error) {
        console.error('Error saving user details:', error);
        return null;
    }
}

// Load session ID from sessionStorage
export function loadSessionId() {
    return sessionStorage.getItem(getKey('session_id')) || '';
}

// Save session ID to sessionStorage
export function saveSessionId(sessionId) {
    sessionStorage.setItem(getKey('session_id'), sessionId);
}

// Load property ID from sessionStorage
// Note: This is the server-assigned property UUID, needed for sendBeacon calls
export function loadPropertyId() {
    return sessionStorage.getItem(getKey('property_id')) || '';
}

// Save property ID to sessionStorage
export function savePropertyId(propertyId) {
    if (propertyId) {
        sessionStorage.setItem(getKey('property_id'), propertyId);
    }
}

// Load user details captured flag
export function loadUserDetailsCaptured() {
    return sessionStorage.getItem(getKey('user_details_captured')) === 'true';
}

// Save user details captured flag
export function saveUserDetailsCaptured(captured) {
    sessionStorage.setItem(getKey('user_details_captured'), captured ? 'true' : 'false');
}

// Load suggested questions shown flag
export function loadShownSuggestions() {
    return sessionStorage.getItem(getKey('shown_suggestions')) === 'true';
}

// Save suggested questions shown flag
export function saveShownSuggestions(shown) {
    sessionStorage.setItem(getKey('shown_suggestions'), shown ? 'true' : 'false');
}

// Load reopen after redirect flag
export function loadReopenAfterRedirect() {
    const value = sessionStorage.getItem(getKey('reopen_after_redirect'));
    // Clear it after reading (one-time flag)
    if (value) {
        sessionStorage.removeItem(getKey('reopen_after_redirect'));
    }
    return value === 'true';
}

// Save reopen after redirect flag
export function saveReopenAfterRedirect(reopen) {
    sessionStorage.setItem(getKey('reopen_after_redirect'), reopen ? 'true' : 'false');
}

// Load manually closed flag
export function loadManuallyClosed() {
    return sessionStorage.getItem(getKey('manually_closed')) === 'true';
}

// Save manually closed flag
export function saveManuallyClosed(closed) {
    if (closed) {
        sessionStorage.setItem(getKey('manually_closed'), 'true');
    } else {
        sessionStorage.removeItem(getKey('manually_closed'));
    }
}

// Load widget height from localStorage (persists across sessions)
export function loadWidgetHeight() {
    try {
        const saved = localStorage.getItem(getKey('widget_height'));
        return saved ? parseInt(saved, 10) : null;
    } catch (error) {
        console.error('Error loading widget height:', error);
        return null;
    }
}

// Save widget height to localStorage (persists across sessions)
export function saveWidgetHeight(height) {
    try {
        localStorage.setItem(getKey('widget_height'), String(height));
    } catch (error) {
        console.error('Error saving widget height:', error);
    }
}

// Initialize state for ChatWidget instance
export function initializeState(config) {
    // Set the storage key prefix based on propertyID from config
    // This ensures session isolation between properties on the same origin
    if (config.propertyID) {
        setStorageKeyPrefix(config.propertyID);
    }

    return {
        sessionId: loadSessionId(),
        messages: loadMessages(),
        storedPropertyId: loadPropertyId(), // Server-assigned property UUID for sendBeacon
        isOpen: false,
        isTyping: false,
        userDetailsCaptured: loadUserDetailsCaptured(),
        userDetails: loadUserDetails(),
        pendingFirstMessage: null,
        pendingCaptureDetailsResponse: null,
        pendingCaptureDetailsMessage: null,
        pendingCaptureAppointmentResponse: null,
        pendingSendConversationResponse: null,
        pendingTextResponse: null,
        pendingRuleId: null,
        currentOptions: null,
        hasShownSuggestedQuestions: loadShownSuggestions(),
        pendingAppointmentEmails: null,
        pendingConversationEmails: null,
        hasRetriedAfterError: false
    };
}
